// src/admin/layouts/AdminLayout.tsx

import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Layout,
  Menu,
  Button,
  Dropdown,
  Badge,
  MessagePlugin,
} from 'tdesign-react';
import {
  DashboardIcon,
  UserIcon,
  UsergroupIcon,
  ViewListIcon,
  FileIcon,
  ChartIcon,
  ShopIcon,
  CartIcon,
  VerifyIcon,
  SettingIcon,
  PoweroffIcon,
  NotificationIcon,
  BillIcon,
  MenuIcon,
  InternetIcon,
  ImageIcon,
} from 'tdesign-icons-react';
import type { MenuValue } from 'tdesign-react';
import { MessageProvider } from '@/admin/components/GlobalMessage';
import { userApi } from '@/user/api';
import { meApi, menuApi } from '@/admin/api';
import type { Menu as MenuType } from '@/admin/types';
import { filterMenusByPermissions } from '@/admin/utils/menu';

const { Header, Aside, Content } = Layout;
const { MenuItem, SubMenu } = Menu;

const iconMap: Record<string, React.ReactNode> = {
  dashboard: <DashboardIcon />,
  setting: <SettingIcon />,
  user: <UserIcon />,
  usergroup: <UsergroupIcon />,
  'view-list': <ViewListIcon />,
  file: <FileIcon />,
  chart: <ChartIcon />,
  shop: <ShopIcon />,
  cart: <CartIcon />,
  verify: <VerifyIcon />,
  bill: <BillIcon />,
  notification: <NotificationIcon />,
  poweroff: <PoweroffIcon />,
  menu: <MenuIcon />,
  internet: <InternetIcon />,
};

const convertBackendMenus = (menus: MenuType[]): any[] => {
  return menus
    .filter((m) => m.menuType !== 3)
    .map((m) => {
      const children = m.children && m.children.length > 0
        ? convertBackendMenus(m.children)
        : undefined;
      return {
        key: m.path || String(m.id),
        label: m.menuName,
        icon: m.icon ? iconMap[m.icon] : null,
        children,
      };
    });
};

// ... menuItems 定义不变 ...
const menuItems = [
  {
    key: '/admin',
    icon: <DashboardIcon />,
    label: '控制台',
  },
  {
    key: 'system',
    icon: <SettingIcon />,
    label: '系统管理',
    children: [
      { key: '/admin/system/user', icon: <UserIcon />, label: '用户管理' },
      { key: '/admin/system/role', icon: <UsergroupIcon />, label: '角色管理' },
      { key: '/admin/system/menu', icon: <ViewListIcon />, label: '菜单管理' },
      { key: '/admin/system/visit-log', icon: <InternetIcon />, label: '访客日志' },
      { key: '/admin/system/banner', icon: <ImageIcon />, label: '轮播图管理' },
    ],
  },
  {
    key: 'business',
    icon: <FileIcon />,
    label: '业务管理',
    children: [
      { key: '/admin/business/article', icon: <FileIcon />, label: '资讯管理' },
      { key: '/admin/business/marketing', icon: <ChartIcon />, label: '营销资料' },
      { key: '/admin/business/product-intro', icon: <FileIcon />, label: '产品介绍管理' },
      { key: '/admin/business/ecard', icon: <FileIcon />, label: '电子名片管理' },
    ],
  },
  {
    key: 'mall',
    icon: <ShopIcon />,
    label: '商城业务',
    children: [
      { key: '/admin/mall/product', icon: <VerifyIcon />, label: '商品审核' },
      { key: '/admin/mall/order-audit', icon: <BillIcon />, label: '订单管理' },
      { key: '/admin/mall/supplier', icon: <ShopIcon />, label: '供应商档案' },
      { key: '/admin/mall/buyer', icon: <CartIcon />, label: '采购商档案' },
      { key: '/admin/mall/fee-payment', icon: <BillIcon />, label: '服务费管理' },
    ],
  },
  {
    key: 'supplier',
    icon: <ShopIcon />,
    label: '供应商管理',
    children: [
      { key: '/admin/supplier/platform', icon: <ShopIcon />, label: '供应商平台' },
    ],
  },
  {
    key: 'ecardManage',
    icon: <FileIcon />,
    label: '电子名片管理',
    children: [
      { key: '/admin/ecardManage/index', icon: <FileIcon />, label: '电子名片管理' },
      { key: '/admin/ecardManage/department', icon: <FileIcon />, label: '部门管理' },
      { key: '/admin/ecardManage/homepage', icon: <FileIcon />, label: '首页配置' },
      { key: '/admin/ecardManage/micrOfficialWebsite', icon: <FileIcon />, label: '微信官方网站' },
      { key: '/admin/ecardManage/employee', icon: <FileIcon />, label: '员工管理' },
      { key: '/admin/ecardManage/theme', icon: <FileIcon />, label: '主题配置' },
      { key: '/admin/ecardManage/electronicBusinessCards', icon: <FileIcon />, label: '电子名片' },
    ],
  },
];

function AdminLayoutInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [activeMenu, setActiveMenu] = useState<MenuValue>(location.pathname || '/admin');
  const [adminInfo, setAdminInfo] = useState<any>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [menuItemsState, setMenuItemsState] = useState<any[]>(menuItems);

  // 【新增】监听窗口大小，小屏幕自动折叠侧边栏
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setCollapsed(true);
      }
    };
    
    // 初始化检查
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const info = localStorage.getItem('adminInfo');
    if (!info) {
      navigate('/admin/login');
      return;
    }
    setAdminInfo(JSON.parse(info));
  }, [navigate]);

  useEffect(() => {
    const loadMenus = async () => {
      try {
        // 优先从缓存读取，减少页面闪烁
        const cached = localStorage.getItem('adminMenus');
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (Array.isArray(parsed)) {
              setMenuItemsState(convertBackendMenus(parsed));
            }
          } catch {
            // 缓存解析失败则忽略
          }
        }

        const res = await meApi.getPermissions();
        if (res.code === 200) {
          const { menus, permissions } = res.data || {};

          if (menus && menus.length > 0) {
            // 后端直接返回了权限菜单树，直接使用
            setMenuItemsState(convertBackendMenus(menus));
            localStorage.setItem('adminMenus', JSON.stringify(menus));
          } else if (permissions && permissions.length > 0) {
            // menus 为空，需要根据 permissions 过滤完整菜单树
            const allMenusRes = await menuApi.getEnabledTree();
            if (allMenusRes.code === 200) {
              const allMenus = allMenusRes.data || [];
              const filtered = filterMenusByPermissions(allMenus, permissions);
              setMenuItemsState(convertBackendMenus(filtered));
              localStorage.setItem('adminMenus', JSON.stringify(filtered));
            }
          } else {
            // 没有任何权限，显示空菜单
            setMenuItemsState([]);
            localStorage.setItem('adminMenus', JSON.stringify([]));
          }
        }
      } catch {
        // 接口失败时保留缓存或静态菜单
      }
    };
    loadMenus();
  }, []);

  useEffect(() => {
    setActiveMenu(location.pathname);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await userApi.logout();
      localStorage.removeItem('adminMenus');
      MessagePlugin.success('退出登录成功');
      navigate('/admin/login');
    } catch {
      MessagePlugin.error('退出登录失败');
    }
  };

  const userDropdownItems = [
    { content: '个人设置', value: 'settings' },
    { content: '退出登录', value: 'logout', onClick: handleLogout },
  ];

  const renderMenuItems = (items: any[]) => {
    return items.map((item) => {
      if (item.children && item.children.length > 0) {
        return (
          <SubMenu
            key={item.key}
            value={item.key}
            title={item.label}
            icon={item.icon}
          >
            {renderMenuItems(item.children)}
          </SubMenu>
        );
      }
      return (
        <MenuItem
          key={item.key}
          value={item.key}
          icon={item.icon}
          onClick={() => {
            if (typeof item.key === 'string' && item.key.startsWith('/')) {
              navigate(item.key);
            }
          }}
        >
          {item.label}
        </MenuItem>
      );
    });
  };

  const asideWidth = collapsed ? 64 : 220;

  return (
    <Layout style={{ minHeight: '100vh', width: '100%', display: 'flex' }}>
      {/* 
         【修改】移除 position: fixed，改为 Flex 子项 
         这样侧边栏和主内容区会自然并列，不会重叠
      */}
      <Aside
        style={{
          width: asideWidth,
          minWidth: asideWidth, // 确保不被压缩
          transition: 'width 0.3s ease, min-width 0.3s ease',
          backgroundColor: '#1a1a2e',
          overflow: 'hidden',
          height: '100vh',
          position: 'sticky', // 使用 sticky 替代 fixed，配合父级 flex 布局
          top: 0,
          zIndex: 100,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          flexShrink: 0,
        }}>
          <span style={{ color: '#fff', fontSize: 18, fontWeight: 'bold', whiteSpace: 'nowrap' }}>
            {collapsed ? '数' : '互联网生态平台'}
          </span>
        </div>

        <Menu
          theme="dark"
          value={activeMenu}
          collapsed={collapsed}
          style={{
            backgroundColor: 'transparent',
            borderRight: 'none',
            flex: 1, // 让菜单占据剩余高度
            overflowY: 'auto',
          }}
        >
          {renderMenuItems(menuItemsState)}
        </Menu>
      </Aside>

      <Layout
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0, // 【关键】防止 Flex 子项内容溢出导致布局撑破
        }}
      >
        <Header style={{
          backgroundColor: '#fff',
          boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px',
          height: 64,
          position: 'sticky',
          top: 0,
          zIndex: 90,
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Button
              variant="text"
              shape="square"
              icon={<ViewListIcon />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ color: '#666' }}
            />
            <span style={{ color: '#666', fontSize: 14, whiteSpace: 'nowrap' }}>管理后台</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Badge count={pendingCount} dot>
              <Button variant="text" shape="square" icon={<NotificationIcon />} style={{ color: '#666' }} />
            </Badge>

            <Dropdown options={userDropdownItems}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  backgroundColor: '#1c5bff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: 14,
                  flexShrink: 0,
                }}>
                  {adminInfo?.username?.charAt(0).toUpperCase() || 'A'}
                </div>
                {!collapsed && window.innerWidth > 768 && (
                   <span style={{ color: '#333', fontSize: 14, whiteSpace: 'nowrap' }}>{adminInfo?.username || '管理员'}</span>
                )}
              </div>
            </Dropdown>

            <Button
              variant="text"
              shape="square"
              icon={<PoweroffIcon />}
              onClick={handleLogout}
              style={{ color: '#666' }}
            />
          </div>
        </Header>

        {/* 
           【修改】Content 区域优化 
           1. flex: 1 确保占据剩余垂直空间
           2. overflow: auto 确保内容过多时出现滚动条，而不是撑破页面
           3. minWidth: 0 配合父级防止溢出
        */}
        <Content style={{
          backgroundColor: '#f5f5f5',
          padding: 24,
          flex: 1,
          overflow: 'auto',
          position: 'relative',
        }}>
          {/* 
             【新增】包裹层，确保内容最小宽度，防止在小屏幕上过度挤压 
             如果内容非常宽（如大表格），这里会触发水平滚动条，保证内容可见
          */}
          {/* minWidth: 'fit-content',  */}
          <div style={{ width: '100%' }}>
             <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
}

export default function AdminLayout() {
  return (
    <MessageProvider>
      <AdminLayoutInner />
    </MessageProvider>
  );
}